// TODO(anthony-knostic): useFeatureFlags isError is dropped here — Flagsmith outage silently hides integrations. Pre-existing; needs its own ticket.
import { useParams } from 'react-router-dom';

import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import {
  useCanViewAdminContent,
  useIsOwnerOrManager,
} from '@/hooks/useRoleCheck';
import {
  flagsmithFeatureFlags,
  type FlagsmithFeatureFlag,
} from '@/lib/flagsmith';

export type IntegrationAccessResult =
  | {
      canRender: true;
      canWrite: boolean;
      organizationId: string;
      isLoading: false;
    }
  | {
      canRender: false;
      canWrite: false;
      organizationId: string | undefined;
      isLoading: boolean;
    };

// Access in a state that warrants rendering UI (either the integration itself
// or a loading skeleton). Excludes terminal denial (canRender:false,
// isLoading:false), which is filtered out at the panel level so integration
// components don't need a redundant null branch.
export type RenderableIntegrationAccess =
  | {
      canRender: true;
      canWrite: boolean;
      organizationId: string;
      isLoading: false;
    }
  | {
      canRender: false;
      canWrite: false;
      organizationId: string | undefined;
      isLoading: true;
    };

export function isRenderable(
  access: IntegrationAccessResult
): access is RenderableIntegrationAccess {
  return access.canRender || access.isLoading;
}

/** Narrowed access type once canRender is confirmed true. */
export type GrantedIntegrationAccess = Extract<
  RenderableIntegrationAccess,
  { canRender: true }
>;

interface UseAccessOptions {
  flag?: FlagsmithFeatureFlag;
  defaultEnabled?: boolean;
}

const NO_FLAGS: string[] = [];

/**
 * Resolves whether a feature flag is enabled given the current flag map and
 * a fallback default. When no flag is provided, access is unrestricted.
 */
export function resolveFlagEnabled(
  flags: Record<string, boolean>,
  flag: FlagsmithFeatureFlag | undefined,
  defaultEnabled: boolean
): boolean {
  if (!flag) return true;
  const v = flags[flag];
  return v ?? defaultEnabled;
}

/**
 * Pure derivation of whether an integration card can render: requires loading
 * to be finished, an org id, an authorized role, and an enabled flag.
 */
export function computeCanRender(args: {
  isLoading: boolean;
  organizationId: string | undefined;
  hasRole: boolean;
  flagEnabled: boolean;
}): boolean {
  return (
    !args.isLoading && !!args.organizationId && args.hasRole && args.flagEnabled
  );
}

function useAccess(options: UseAccessOptions): IntegrationAccessResult {
  const { flag, defaultEnabled = false } = options;
  const { organizationId } = useParams<{ organizationId: string }>();

  // Read-scope: owner, manager, or support can render the integration cards.
  const { canViewAdminContent, isLoading: roleLoading } =
    useCanViewAdminContent(organizationId);

  // Write-scope: only owner or manager may connect/disconnect integrations.
  const { isOwnerOrManager } = useIsOwnerOrManager(organizationId);

  const flagKeys = flag ? [flag] : NO_FLAGS;
  const { flags, isLoading: flagsLoading } = useFeatureFlags(flagKeys);

  const hasRole = canViewAdminContent; // read-scope: owner/manager/support
  const canWrite = isOwnerOrManager; // write-scope: owner/manager only
  const flagEnabled = resolveFlagEnabled(flags, flag, defaultEnabled);
  const isLoading = roleLoading || (flag !== undefined && flagsLoading);
  const canRender = computeCanRender({
    isLoading,
    organizationId,
    hasRole,
    flagEnabled,
  });

  if (canRender && organizationId) {
    return { canRender: true, canWrite, organizationId, isLoading: false };
  }
  return { canRender: false, canWrite: false, organizationId, isLoading };
}

export function useSlackIntegrationAccess(): IntegrationAccessResult {
  return useAccess({
    flag: flagsmithFeatureFlags.SLACK_SCAN_LOGS_EVENTS,
    defaultEnabled: true,
  });
}

export function useGithubIntegrationAccess(): IntegrationAccessResult {
  return useAccess({ flag: flagsmithFeatureFlags.GITHUB_INTEGRATION });
}

export function useBitbucketIntegrationAccess(): IntegrationAccessResult {
  return useAccess({
    flag: flagsmithFeatureFlags.BITBUCKET_INTEGRATION,
  });
}

export function useSiemIntegrationAccess(): IntegrationAccessResult {
  return useAccess({
    flag: flagsmithFeatureFlags.SIEM_INTEGRATION,
  });
}

export function useSamlSsoIntegrationAccess(): IntegrationAccessResult {
  return useAccess({
    flag: flagsmithFeatureFlags.SSO_LOGIN,
  });
}

export function useScimProvisioningIntegrationAccess(): IntegrationAccessResult {
  // No feature flag (KIR-4693) — role-gated only; config-gated server-side by
  // DESCOPE_SCIM_BASE_URL (the modal shows "not configured" when it's unset).
  return useAccess({});
}
